import { useEffect, useState } from "react";
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../utils/api";
import { usePraxis } from "../context/PraxisContext";
import { getIconComponent } from "../data/icons";
import IconPicker from "./IconPicker";

/**
 * Admin-Tab „Menopause": komplette Wechseljahre-Seite bearbeitbar,
 * ausschließlich mit Klartext-Feldern (KEIN HTML für Autoren nötig).
 * Formatierungen (Zitat vs. Absatz) über einen einfachen Typ-Selektor pro Zeile.
 */
export default function MenopauseManager() {
  const { menopause, refresh } = usePraxis();
  const [values, setValues] = useState({
    hero_label: "", hero_title: "", hero_em: "", hero_lead: "",
    understand_label: "", understand_title: "", understand_subtitle: "",
    paragraphs: [],
    ablauf_label: "", ablauf_title: "", ablauf_lead: "",
    steps: [],
    fineprint: "",
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (menopause) setValues((prev) => ({ ...prev, ...menopause }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menopause]);

  const set = (patch) => setValues({ ...values, ...patch });

  const save = async () => {
    setError(""); setMessage("");
    setSaving(true);
    const payload = {
      menopause_hero_label: values.hero_label,
      menopause_hero_title: values.hero_title,
      menopause_hero_em: values.hero_em,
      menopause_hero_lead: values.hero_lead,
      menopause_understand_label: values.understand_label,
      menopause_understand_title: values.understand_title,
      menopause_understand_subtitle: values.understand_subtitle,
      menopause_paragraphs: JSON.stringify(values.paragraphs || []),
      menopause_ablauf_label: values.ablauf_label,
      menopause_ablauf_title: values.ablauf_title,
      menopause_ablauf_lead: values.ablauf_lead,
      menopause_steps: JSON.stringify(values.steps || []),
      menopause_fineprint: values.fineprint,
    };
    const { data } = await api.updateSettings(payload);
    setSaving(false);
    if (data && data.ok) {
      setMessage("Menopause-Inhalte gespeichert.");
      refresh();
      setTimeout(() => setMessage(""), 4000);
    } else {
      setError("Speichern fehlgeschlagen.");
    }
  };

  return (
    <div data-testid="menopause-manager">
      <div style={{ marginBottom: "1rem" }}>
        <h2 className="admin-list-title" style={{ margin: 0 }}>Menopause / Wechseljahre-Seite</h2>
        <p className="admin-align-hint" style={{ margin: "0.3rem 0 0" }}>
          Alle Inhalte der Seite <code>/wechseljahre</code>. Reine Textbearbeitung — keine HTML-Kenntnisse nötig.
        </p>
      </div>

      {message && <p className="admin-success" data-testid="menopause-message">{message}</p>}
      {error && <p className="admin-error">{error}</p>}

      {/* HERO */}
      <details className="admin-collapse" open>
        <summary>1. Hero (oberer Bereich)</summary>
        <div className="admin-collapse-body">
          <label className="admin-label">
            Kleiner Text über der Überschrift
            <input type="text" value={values.hero_label} onChange={(e) => set({ hero_label: e.target.value })}
                   data-testid="mp-hero-label" maxLength={80}/>
          </label>
          <div className="admin-two-col">
            <label className="admin-label">
              Haupt-Titel
              <input type="text" value={values.hero_title} onChange={(e) => set({ hero_title: e.target.value })}
                     data-testid="mp-hero-title" maxLength={120}/>
            </label>
            <label className="admin-label">
              Untertitel (leer lassen wenn nicht gewünscht)
              <input type="text" value={values.hero_em} onChange={(e) => set({ hero_em: e.target.value })}
                     data-testid="mp-hero-em" maxLength={80}/>
            </label>
          </div>
          <label className="admin-label">
            Einleitungstext
            <textarea value={values.hero_lead} rows={4}
                      onChange={(e) => set({ hero_lead: e.target.value })}
                      data-testid="mp-hero-lead" maxLength={800}/>
          </label>
        </div>
      </details>

      {/* UNDERSTAND */}
      <details className="admin-collapse">
        <summary>2. Fließtext-Bereich („Menopause verstehen&#8220;)</summary>
        <div className="admin-collapse-body">
          <label className="admin-label">
            Kleiner Text über der Überschrift
            <input type="text" value={values.understand_label}
                   onChange={(e) => set({ understand_label: e.target.value })}
                   data-testid="mp-understand-label" maxLength={80}/>
          </label>
          <div className="admin-two-col">
            <label className="admin-label">
              Titel (Zeile 1)
              <input type="text" value={values.understand_title}
                     onChange={(e) => set({ understand_title: e.target.value })}
                     data-testid="mp-understand-title" maxLength={120}/>
            </label>
            <label className="admin-label">
              Untertitel (Zeile 2, optional)
              <input type="text" value={values.understand_subtitle}
                     onChange={(e) => set({ understand_subtitle: e.target.value })}
                     data-testid="mp-understand-subtitle" maxLength={200}/>
            </label>
          </div>

          <div style={{ marginTop: "1rem", marginBottom: "0.5rem" }}>
            <strong>Absätze</strong> — je Zeile ein Absatz, per Auswahl „Zitat&#8220; für hervorgehobene Sätze:
          </div>
          <ParagraphsEditor
            paragraphs={values.paragraphs || []}
            onChange={(paragraphs) => set({ paragraphs })}
          />
        </div>
      </details>

      {/* ABLAUF */}
      <details className="admin-collapse">
        <summary>3. Ablauf-Schritte</summary>
        <div className="admin-collapse-body">
          <div className="admin-two-col">
            <label className="admin-label">
              Kleiner Text über der Überschrift
              <input type="text" value={values.ablauf_label}
                     onChange={(e) => set({ ablauf_label: e.target.value })}
                     data-testid="mp-ablauf-label" maxLength={80}/>
            </label>
            <label className="admin-label">
              Überschrift
              <input type="text" value={values.ablauf_title}
                     onChange={(e) => set({ ablauf_title: e.target.value })}
                     data-testid="mp-ablauf-title" maxLength={120}/>
            </label>
          </div>
          <label className="admin-label">
            Einleitungstext
            <textarea value={values.ablauf_lead} rows={2}
                      onChange={(e) => set({ ablauf_lead: e.target.value })}
                      data-testid="mp-ablauf-lead" maxLength={400}/>
          </label>

          <div style={{ marginTop: "1.5rem", marginBottom: "0.5rem" }}>
            <strong>Schritte</strong> (drag-sortierbar):
          </div>
          <StepsEditor
            steps={values.steps || []}
            onChange={(steps) => set({ steps })}
          />
        </div>
      </details>

      {/* FINEPRINT */}
      <details className="admin-collapse">
        <summary>4. GOÄ-Hinweis (Fineprint am Ende)</summary>
        <div className="admin-collapse-body">
          <label className="admin-label">
            Fineprint-Text (Zeilenumbrüche = neue Zeile, Leerzeile = neuer Absatz)
            <textarea value={values.fineprint} rows={5}
                      onChange={(e) => set({ fineprint: e.target.value })}
                      data-testid="mp-fineprint" maxLength={1000}/>
          </label>
        </div>
      </details>

      <div className="admin-actions-row" style={{ marginTop: "2rem" }}>
        <button type="button" onClick={save} className="admin-btn admin-btn-primary" disabled={saving}
                data-testid="menopause-save">
          {saving ? "Speichere…" : "Alle Menopause-Inhalte speichern"}
        </button>
      </div>
    </div>
  );
}

/* -------- Absatz-Editor: Liste aus {type: 'text'|'quote', text: string} -------- */
function ParagraphsEditor({ paragraphs, onChange }) {
  const addParagraph = (type = "text") => onChange([...paragraphs, { type, text: "" }]);
  const updateParagraph = (idx, patch) => {
    const next = [...paragraphs];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeParagraph = (idx) => onChange(paragraphs.filter((_, i) => i !== idx));
  const moveParagraph = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= paragraphs.length) return;
    const next = [...paragraphs];
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };

  return (
    <div data-testid="mp-paragraphs-list">
      {paragraphs.length === 0 && (
        <p className="admin-empty" style={{ margin: "0.5rem 0" }}>Noch keine Absätze angelegt.</p>
      )}
      {paragraphs.map((p, idx) => (
        <div key={idx} className={`paragraph-row paragraph-row-${p.type === "quote" ? "quote" : "text"}`}
             data-testid={`mp-paragraph-${idx}`}>
          <div className="paragraph-row-controls">
            <select
              value={p.type || "text"}
              onChange={(e) => updateParagraph(idx, { type: e.target.value })}
              data-testid={`mp-paragraph-type-${idx}`}
              className="paragraph-type-select"
            >
              <option value="text">Absatz</option>
              <option value="quote">Zitat (hervorgehoben)</option>
            </select>
            <button type="button" onClick={() => moveParagraph(idx, -1)} disabled={idx === 0}
                    className="admin-btn admin-btn-ghost" title="hoch">↑</button>
            <button type="button" onClick={() => moveParagraph(idx, 1)}
                    disabled={idx === paragraphs.length - 1}
                    className="admin-btn admin-btn-ghost" title="runter">↓</button>
            <button type="button" onClick={() => removeParagraph(idx)}
                    className="admin-btn admin-btn-danger" title="löschen"
                    data-testid={`mp-paragraph-remove-${idx}`}>×</button>
          </div>
          <textarea
            value={p.text || ""}
            onChange={(e) => updateParagraph(idx, { text: e.target.value })}
            rows={p.type === "quote" ? 2 : 4}
            placeholder={p.type === "quote" ? "Kurzes Zitat (wird hervorgehoben angezeigt)" : "Absatztext…"}
            data-testid={`mp-paragraph-text-${idx}`}
            className="paragraph-textarea"
          />
        </div>
      ))}
      <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem" }}>
        <button type="button" onClick={() => addParagraph("text")}
                className="admin-btn admin-btn-primary"
                data-testid="mp-paragraph-add-text">+ Absatz</button>
        <button type="button" onClick={() => addParagraph("quote")}
                className="admin-btn admin-btn-ghost"
                data-testid="mp-paragraph-add-quote">+ Zitat</button>
      </div>
    </div>
  );
}

/* -------- Steps Editor (drag-sort + add/remove) -------- */
function StepsEditor({ steps, onChange }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const addStep = () => onChange([...steps, { icon: "mail", title: "", text: "" }]);
  const updateStep = (idx, patch) => {
    const next = [...steps];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const removeStep = (idx) => onChange(steps.filter((_, i) => i !== idx));

  const withIds = steps.map((s, i) => ({ ...s, _id: `step-${i}` }));

  const onDragEnd = (e) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = withIds.findIndex((i) => i._id === active.id);
    const newIdx = withIds.findIndex((i) => i._id === over.id);
    onChange(arrayMove(steps, oldIdx, newIdx));
  };

  return (
    <>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={withIds.map((i) => i._id)} strategy={verticalListSortingStrategy}>
          <ul className="team-list" data-testid="mp-steps-list">
            {withIds.map((s, idx) => (
              <SortableStepRow key={s._id} step={s} idx={idx}
                onChange={(patch) => updateStep(idx, patch)}
                onRemove={() => removeStep(idx)} />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
      <button type="button" onClick={addStep} className="admin-btn admin-btn-ghost"
              style={{ marginTop: "0.5rem" }} data-testid="mp-step-add">
        + Schritt hinzufügen
      </button>
    </>
  );
}

function SortableStepRow({ step, idx, onChange, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step._id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const Icon = getIconComponent(step.icon);
  return (
    <li ref={setNodeRef} style={style} className="team-list-item mp-step-row" data-testid={`mp-step-${idx}`}>
      <span className="team-drag-handle" {...attributes} {...listeners} title="Ziehen zum Sortieren">
        <svg width="14" height="20" viewBox="0 0 14 20" fill="currentColor" aria-hidden="true">
          <circle cx="4" cy="4" r="1.5"/><circle cx="10" cy="4" r="1.5"/>
          <circle cx="4" cy="10" r="1.5"/><circle cx="10" cy="10" r="1.5"/>
          <circle cx="4" cy="16" r="1.5"/><circle cx="10" cy="16" r="1.5"/>
        </svg>
      </span>
      <div className="service-icon-thumb" aria-hidden="true">
        <Icon size={22} strokeWidth={1.5} />
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: 0 }}>
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: "0.5rem" }}>
          <IconPicker value={step.icon} onChange={(v) => onChange({ icon: v })} />
          <input type="text" value={step.title} placeholder="Titel des Schritts"
                 onChange={(e) => onChange({ title: e.target.value })}
                 style={{ padding: "0.6rem 0.9rem", border: "1px solid #DFD3B8", borderRadius: "10px",
                          background: "#FBF8F3", fontSize: "0.92rem" }}
                 data-testid={`mp-step-title-${idx}`}/>
        </div>
        <textarea value={step.text} placeholder="Erklärungstext" rows={2}
                  onChange={(e) => onChange({ text: e.target.value })}
                  style={{ padding: "0.6rem 0.9rem", border: "1px solid #DFD3B8", borderRadius: "10px",
                           background: "#FBF8F3", fontSize: "0.9rem", resize: "vertical", fontFamily: "inherit" }}
                  data-testid={`mp-step-text-${idx}`}/>
      </div>
      <button type="button" onClick={onRemove} className="admin-btn admin-btn-danger"
              style={{ alignSelf: "flex-start" }} data-testid={`mp-step-remove-${idx}`}>×</button>
    </li>
  );
}
